import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../constants/common'
import { useInlineNotifications, useUserData } from '../../../hooks'
import { parseServerValidation } from '../../../utils/validation'
import { ConfirmationModal } from '../../Confirmation'
import { FormikForm } from '../../Formik'
import { Loader } from '../../Loader'
import { useRequestWizardDraft } from '../Request.hooks'

import { RequestWizardAIWizardButton } from './RequestWizardAIWizardButton'
import { RequestWizardCardStageFormFields } from './RequestWizardCardStageFormFields'
import {
  getChunk as getWizardRequestFormChunk,
  initWizard as initWizardRequestForm,
  prolong as prolongWizard,
  sendPayloadChunk as sendWizardRequestFormPayloadChunk,
} from './RequestWizardForm.api'
import {
  CARD_STAGE,
  INIT_STAGE,
  LOAD_STAGE,
} from './RequestWizardForm.constants'
import {
  useRequestWizardAIWizard,
  useWizardLifecycle,
} from './RequestWizardForm.hooks'
import {
  applyRequestWizardDraftSafely,
  clearWizardUuid,
  getCardStageInitialValues,
  getCardStageSubmitHandler,
  getCardStageValidationSchema,
  getInitStageInitialValues,
  getInitStagePayload,
  getInitStageSubmitHandler,
  getInitStageValidationSchema,
  getRequestWizardChunkCards,
  getRequestWizardChunkCreatedRequests,
  getWizardChunkSignature,
  isRequestWizardCardStage,
  isRequestWizardFinished,
  isRequestWizardInitStage,
  isRequestWizardRestoreStage,
  isWizardDraftStaleForStage,
  parseRequestWizardCreationFormServerError,
} from './RequestWizardForm.utils'
import { RequestWizardInitStageFormFields } from './RequestWizardInitStageFormFields'

function RequestWizardForm({
  FormContainer,
  autoRestore = false,
  cardStageFormFieldsProps,
  chatMessageUuid,
  containerProps,
  initialBuilding,
  initialFlat,
  initialUser,
  initStageFormFieldsProps,
  isChatroomPublic,
  messageUuid,
  originalRequestId,
  postUuid,
  wizardApiProps: {
    getChunk = getWizardRequestFormChunk,
    initWizard = initWizardRequestForm,
    prolong = prolongWizard,
    sendPayloadChunk = sendWizardRequestFormPayloadChunk,
  } = EMPTY_OBJECT,
  onCancel,
  onCardStageClose,
  onClose,
  onError,
  onSuccess,
  ...restProps
}) {
  const { t } = useTranslation()

  const [stage, setStage] = useState(LOAD_STAGE)
  const [chunk, setChunk] = useState(EMPTY_LIST)
  const [uuid, setUuid] = useState(null)
  const [restoredChunk, setRestoredChunk] = useState(null)
  const [createdRequests, setCreatedRequests] = useState(EMPTY_LIST)

  const [isSingleFlat, setIsSingleFlat] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [isInitLoading, setInitLoading] = useState(true)

  const prolongRef = useRef()
  const formikRef = useRef()

  const currentUser = useUserData()

  const { handleAddNotification, handleRemoveNotification, notifications } =
    useInlineNotifications()

  const { draft: wizardDraft, setDraft: setWizardDraft } =
    useRequestWizardDraft()

  const {
    handleGenerate: handleAIWizardGenerate,
    isFilled: isAIWizardFilled,
    isLoading: isAILoading,
    showButton: showAIWizardButton,
  } = useRequestWizardAIWizard({
    chunk,
    formikRef,
    messageUuid,
    stage,
    uuid,
    onRemoveNotification: handleRemoveNotification,
    onSetNotification: handleAddNotification,
  })

  // Latch the draft once on the first non-undefined value. The provider
  // hydrates async (storage read), so capturing `wizardDraft` at mount
  // would freeze `undefined` — losing the saved values for the entire
  // form lifetime. The in-render setState is React's documented pattern
  // for "adjusting state during rendering" (https://react.dev/reference/react/useState).
  const [initialDraft, setInitialDraft] = useState()

  if (initialDraft === undefined && wizardDraft !== undefined) {
    setInitialDraft(wizardDraft)
  }

  const draftValuesForStage = applyRequestWizardDraftSafely(initialDraft, {
    chunk,
    stage,
  })

  useEffect(() => {
    if (
      isWizardDraftStaleForStage({
        chunk,
        draft: initialDraft,
        draftValuesForStage,
        stage,
      })
    ) {
      setWizardDraft(null, 0)
    }
  }, [chunk, draftValuesForStage, initialDraft, setWizardDraft, stage])

  const initialValues = {
    [CARD_STAGE]:
      isRequestWizardCardStage(stage) && draftValuesForStage
        ? draftValuesForStage
        : getCardStageInitialValues(chunk),
    [INIT_STAGE]:
      isRequestWizardInitStage(stage) && draftValuesForStage
        ? draftValuesForStage
        : getInitStageInitialValues({ initialBuilding, initialFlat }),
  }

  const validationSchemas = {
    [CARD_STAGE]: getCardStageValidationSchema(chunk),
    [INIT_STAGE]: getInitStageValidationSchema(),
  }

  function handleReceiveChunk(chunk) {
    const cards = getRequestWizardChunkCards(chunk)

    setChunk(cards)
    setCreatedRequests(getRequestWizardChunkCreatedRequests(chunk))

    return cards
  }

  function handleInitWizard({ data, setErrors } = EMPTY_OBJECT) {
    setLoading(true)

    return initWizard(getInitStagePayload({ originalRequestId }))
      .then(({ data }) => {
        const { uuid } = data

        setUuid(uuid)

        return uuid
      })
      .then(uuid => sendPayloadChunk(uuid, data))
      .then(({ data }) => {
        handleReceiveChunk(data)
        setStage(CARD_STAGE)
        setLoading(false)
      })
      .catch(error => {
        setErrors?.(
          parseServerValidation(
            parseRequestWizardCreationFormServerError(error?.response?.data, t)
          )
        )
        onError?.()
        setLoading(false)
      })
  }

  const isWorkflowFinished = isRequestWizardFinished({ chunk, stage })

  useWizardLifecycle({
    autoRestore,
    getChunk,
    isWorkflowFinished,
    prolong,
    prolongRef,
    setChunk,
    setInitLoading,
    setRestoredChunk,
    setStage,
    setUuid,
    uuid,
  })

  function handleSubmitChunk({ data, resetForm, setErrors, uuid }) {
    setLoading(true)

    return sendPayloadChunk(uuid, data)
      .then(({ data }) => {
        const cards = handleReceiveChunk(data)

        if (cards.length === 0) {
          // Final chunk submitted — the wizard is complete on the server.
          setWizardDraft(null, 0)
        }

        resetForm?.()
      })
      .catch(error => {
        setErrors?.(
          parseServerValidation(
            parseRequestWizardCreationFormServerError(error?.response?.data, t)
          )
        )
        onError?.()
      })
      .finally(() => {
        setLoading(false)
      })
  }

  function handleCancelRestore() {
    setWizardDraft(null, 0)
    setUuid(null)
    setChunk(null)
    clearWizardUuid()

    setStage(INIT_STAGE)
  }

  function handleRestore() {
    setChunk(restoredChunk)
    setStage(CARD_STAGE)
  }

  function handleFinish() {
    setWizardDraft(null, 0)
    onClose?.()
    onSuccess?.(createdRequests)
  }

  function getDraftPayload(values) {
    return {
      chunkSignature: getWizardChunkSignature(chunk),
      stage,
      uuid,
      values,
    }
  }

  const submitHandlers = {
    [CARD_STAGE]: getCardStageSubmitHandler({
      chunk,
      isChatroomPublic,
      uuid,
      onSubmit: handleSubmitChunk,
    }),
    [INIT_STAGE]: getInitStageSubmitHandler({
      chatMessageUuid,
      currentUser,
      isChatroomPublic,
      isSingleFlat,
      messageUuid,
      postUuid,
      onInit: handleInitWizard,
    }),
  }

  return isInitLoading ? (
    <Loader />
  ) : isRequestWizardRestoreStage(stage) ? (
    <ConfirmationModal
      actionButtonsProps={{
        cancelText: t('Modal.WizardNewBtnKey'),
        submitText: t('Modal.WizardRestoreBtnKey'),
      }}
      description={t('Modal.WizardRestoreConfirmationTextKey')}
      onCancel={handleCancelRestore}
      onSubmit={handleRestore}
    />
  ) : (
    <FormikForm
      enableReinitialize
      Container={FormContainer}
      containerProps={{
        ...containerProps,
        getDraftPayload,
        headerProps: {
          ...containerProps?.headerProps,
          actions: showAIWizardButton ? (
            <RequestWizardAIWizardButton
              isFilled={isAIWizardFilled}
              isLoading={isAILoading}
              isSubmitting={isLoading}
              onGenerate={handleAIWizardGenerate}
            />
          ) : (
            containerProps?.headerProps?.actions
          ),
        },
        onClose: isWorkflowFinished
          ? handleFinish
          : isRequestWizardCardStage(stage) && onCardStageClose
            ? onCardStageClose
            : onClose,
        onMinimize:
          isWorkflowFinished || isLoading
            ? undefined
            : containerProps?.onMinimize,
        onSubmitConfirm: onClose,
      }}
      initialValues={initialValues[stage]}
      innerRef={formikRef}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchemas[stage]}
      onSubmit={submitHandlers[stage]}
    >
      {isRequestWizardInitStage(stage) ? (
        <RequestWizardInitStageFormFields
          {...restProps}
          {...initStageFormFieldsProps}
          initialUser={initialUser}
          isAILoading={isAILoading}
          isCardLoading={isLoading || isAILoading}
          isSingleFlat={isSingleFlat}
          notifications={notifications}
          setIsSingleFlat={setIsSingleFlat}
          onCancel={onCancel}
          onClose={onClose}
          onRemoveNotification={handleRemoveNotification}
          onSetNotification={handleAddNotification}
        />
      ) : null}
      {isRequestWizardCardStage(stage) ? (
        <RequestWizardCardStageFormFields
          {...restProps}
          {...cardStageFormFieldsProps}
          chunk={chunk}
          isAILoading={isAILoading}
          isCardLoading={isLoading || isAILoading}
          notifications={notifications}
          uuid={uuid}
          onCancel={onCancel}
          onFinish={handleFinish}
          onRemoveNotification={handleRemoveNotification}
          onSetNotification={handleAddNotification}
        />
      ) : null}
    </FormikForm>
  )
}

export default RequestWizardForm
